import Vue from 'vue';
import { Store } from 'vuex';

import { ObjectType } from '@/@types/graphql-types';

import { OnboardingStatus } from '@/enums/onboarding-status';

import Provider from '@/interfaces/provider';
import { HtmlContentMovieDetails, UrlMetadataResponse } from '@/interfaces/responses/url-metadata';
import { Episode, NewTitle, Season, TitleObjectType } from '@/interfaces/titles';

import {
	LegacySnowplowPushContext,
	SnowplowCampaignContextV1,
	SnowplowCampaignContextV2,
	SnowplowContext,
	SnowplowCoreWebVitalsContext,
	SnowplowDevelopmentTestContext,
	SnowplowFilterContext,
	SnowplowFreemiumContext,
	SnowplowLoginContext,
	SnowplowMobileContext,
	SnowplowModuleContext,
	SnowplowPackageContext,
	SnowplowPageTypeContext,
	SnowplowPushContext,
	SnowplowSchema,
	SnowplowTitleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingProvider, TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers/tracking-provider';

import { GDPR_WEB_LOCALES } from '@/constants/web-locales.constant';
import { WebLocale } from '@/enums/web-locale';
import { TitleListName } from '@/interfaces/responses/title-list';
import { ReadyType } from '@/stores/ready.store';
import { Consent, CONSENT_PARTNERS } from '@/stores/user.store';

import type { Metric } from 'web-vitals';

interface TrackingSession {
	context: SnowplowCampaignContextV1 | SnowplowCampaignContextV2 | LegacySnowplowPushContext | SnowplowPushContext;
	timestamp: number;
}

export type TrackingListEventPayload = { action: string; property?: string | 'similartitles' };

/**
 * Use context schema name to avoid duplicate types
 * Usage: as argument to Array.filter() method
 */
const removeDuplicateByType = (baseList: SnowplowSchema[]) => (element: SnowplowContext) => {
	const type = element.getSchema();
	return !baseList.includes(type);
};

let permanent_audiences: string[] = [];

export const setPermanentAudiences = (pa: string[]) => {
	permanent_audiences = pa;
};

export const resetPermanentAudiences = () => {
	permanent_audiences = [];
};

/**
 * Simple wrapper around different event tracking services which provides
 * a streamlined interface for tracking events and page views.
 */
class TrackingHelperClass {
	private trackers: TrackingProvider[] = [];
	private isInitialized = false;
	private store: Store<any>;
	private sessions: TrackingSession[] = [];
	private sessionDuration = 60 * 30 * 1000; // 30 minutes
	private notBounced = false;
	private mobileContext: SnowplowMobileContext;

	/**
	 * interim solution to resets the global state of the tracking helper.
	 * in a perfect world we have the trackinghelper included/merged and reinitialised in the tracking store.
	 */
	resetState() {
		this.sessions = [];
		this.notBounced = false;
	}

	get isDisabled(): boolean {
		return !!process.env.VUE_APP_BOOK;
	}

	setStore(store: Store<any>) {
		this.store = store;
	}

	/**
	 * jwId has been resolved and will now be used to initialize all the trackers and process the queue.
	 */
	async initializeTrackers(trackers: TrackingProvider[], force: boolean = false) {
		const { store } = this;
		const { language } = store.state;
		const isGDPRCountry = this.isGDPRCountry(language.webLocale);

		// await with loading tracking libs until any consent is accepted or rejected
		if (!global.VUE_APP_TESTS_E2E) {
			if (!force) await Vue.$jw.ready?.waitFor(ReadyType.CONSENT_PARTNERS_ANSWERED);
		}

		const initTrackerPromises = trackers
			.filter(
				tracker =>
					// either it's not in a GDPR country
					!isGDPRCountry ||
					// or it is and its DPS has been consented to
					(isGDPRCountry &&
						(tracker.dataProcessingService === null ||
							store.getters['user/dpsConsents']![tracker.dataProcessingService]))
			)
			.map(async (tracker: TrackingProvider): Promise<TrackingProvider | boolean> => {
				try {
					await tracker.initialize();
					return tracker;
				} catch (err) {
					console.error('[Tracker runtime error: initialize', err);
					return true;
				}
			});

		this.trackers = (await Promise.all(initTrackerPromises)).filter(t => t !== true) as TrackingProvider[];
		this.isInitialized = true;

		// possibly add campaign contexts
		await this.handleAddingCampaignContexts();

		this.processQueue();
	}

	async registerTracker(newTracker: TrackingProvider) {
		// check if tracker is already initialized
		const existingTracker = this.trackers.find(tracker => tracker.constructor === newTracker.constructor);
		if (existingTracker) return;

		await newTracker.initialize();
		this.trackers.push(newTracker);
	}

	async unRegisterTracker(tracker: TrackingProvider) {
		// filter out tracker to be unregistered
		this.trackers = this.trackers.filter(existingTracker => existingTracker.constructor !== tracker.constructor);
	}

	private async processQueue() {
		// MERGE NOTE: re enable this for snowplow upgrade as experiment
		// await Vue.$jw.ready?.waitFor(ReadyType.SNOWPLOW_INIT);

		const { store } = this;
		if (this.isInitialized) {
			// page views
			store.getters['tracking/getQueueOf']('pageViews').forEach((properties: any) => {
				this.trackers.forEach((tracker: TrackingProvider) => {
					try {
						tracker.trackPage.apply(tracker, [properties]);
					} catch (err) {
						console.error('[Tracker runtime error: pageViews', err);
					}
				});
			});

			this.store.dispatch('tracking/resetQueue', 'pageViews');

			// events
			store.getters['tracking/getQueueOf']('events').forEach((event: any) => {
				const { category, properties, contexts } = event;
				this.trackers.forEach((tracker: TrackingProvider) => {
					try {
						tracker.trackEvent(category, properties, contexts);
					} catch (err) {
						console.error('[Tracker runtime error: events', err);
					}
				});
			});

			this.store.dispatch('tracking/resetQueue', 'events');
		}
	}

	async trackPage(path: string, additionalContexts: SnowplowContext[] = []) {
		const { store } = this;
		const { routing } = this.store.state;
		const { tracking } = this.store.state;

		const title = routing.urlMetadata?.meta_title || 'Justwatch';

		// wait for metaDataPromise
		await routing.urlMetadataPromise;

		// Refferal Module Context
		const referralModuleContext: SnowplowModuleContext = store.getters['titleDetails/refferalModuleContext'];
		const pageContexts: SnowplowContext[] = referralModuleContext ? [referralModuleContext] : [];
		let contexts: SnowplowContext[] = await this.getContexts(pageContexts, path);

		if (additionalContexts.length) {
			contexts = contexts.concat(additionalContexts);
		}

		const pageViewEvent = { path, title, permanent_audiences, contexts };
		this.store.dispatch('tracking/addPageView', pageViewEvent);

		if (tracking.pagesVisited > 1) {
			this.checkNotBounced();
		}

		this.processQueue();
	}

	/**
	 * Tracks a structured event.
	 * NOTE: Use without await, as it doesn't make sense for user to wait on this.
	 * @TODO check if tracking is disabled in consents
	 */
	async trackEvent(
		category: string,
		properties: TrackingProviderPropertiesInterface,
		contexts: SnowplowContext[] = [],
		force: boolean = false
	) {
		if (this.isDisabled) {
			return;
		}

		const { routing } = this.store.state;

		// argument validation
		if (!category) {
			throw new Error('Expected "category" to be a non-empty string value');
		}

		// make sure the correct urlMetadata is loaded and is not fired too early, too
		if (!force) await routing.urlMetadataPromise;

		properties.path = routing.activeRoute?.fullPath;
		if (!properties.path) {
			return;
		}
		contexts = await this.getContexts(contexts, properties.path, properties.useLastRoute);

		const trackEvent = { category, properties, contexts };
		this.store.dispatch('tracking/addTrackEvent', trackEvent);

		if (!properties.nonInteraction) {
			this.checkNotBounced();
		}

		this.processQueue();

		// TODO: remove the if-statement as soon as the reason for this problem
		// snowplow tracking error is figured out
		// reference trello card 121-snowplow-tracking-erroneous-home-page-view-events
		// if (category === 'home_page_view' && properties.path !== '/') {
		// 	const currentEvent = {
		// 		category,
		// 		properties,
		// 	};
		// 	captureMessageForSentry('Home page view fired with wrong path', currentEvent);
		// 	console.log('Home page view fired with wrong path', properties.path, currentEvent);
		// }
	}

	async trackConsent(consents: Record<Consent, boolean>) {
		if (typeof window === 'undefined') {
			return Promise.resolve('Unknown');
		}

		(Object.keys(consents) as Consent[]).forEach(consentName => {
			const consentType = consents[consentName] ? 'trackConsentGranted' : 'trackConsentWithdrawn';
			const consentId = CONSENT_PARTNERS[consentName].id;
			const all = false;
			const version = '1';
			const name = null;
			const description = null;
			const date = new Date();
			date.setMonth(date.getMonth() + 50);
			const expiry = date.toISOString();

			if (consentType === 'trackConsentGranted') {
				window.snowplow(consentType, consentId, version, name, description, expiry, []);
			} else if (consentType === 'trackConsentWithdrawn') {
				window.snowplow(consentType, all, consentId, version, name, description, []);
			}
		});
	}

	async trackFilterToggleEvent(
		eventProperties: { label: string; property: any; value?: 0 | 1 },
		isPremiumFilter: boolean
	) {
		const { label, property, value } = eventProperties;

		const { FreemiumHelper } = await import('@/helpers/iap-helper');

		this.trackEvent(
			'userinteraction',
			{
				action: 'filter_toggled',
				label,
				property: property ?? 'all',
				value,
			},
			isPremiumFilter ? [FreemiumHelper.getFreemiumContext()] : []
		);
	}

	async trackListEvent(
		listName: TitleListName,
		listAction: 'add' | 'remove',
		payload: TrackingListEventPayload,
		contexts: SnowplowContext[]
	) {
		this.trackEvent(`${listName}_${listAction}`, payload, contexts);
	}

	async trackFreemiumEvent(
		category: string,
		properties: TrackingProviderPropertiesInterface,
		contexts: SnowplowContext[] = []
	) {
		const { FreemiumHelper } = await import('@/helpers/iap-helper');
		this.trackEvent(category, properties, [FreemiumHelper.getFreemiumContext(), ...contexts]);
	}

	async getContexts(
		contexts: SnowplowContext[],
		pathname?: string,
		useLastRoute = false
	): Promise<SnowplowContext[]> {
		// create an array of Snowplow context type (strings)
		const typeList = contexts.map(context => context.getSchema());

		const baseContexts = await this.getBaseContexts(pathname, useLastRoute);
		const filteredBaseContexts = baseContexts.filter(removeDuplicateByType(typeList));
		const additionalContexts = this.getAdditionalContexts().filter(removeDuplicateByType(typeList));

		return [...contexts, ...filteredBaseContexts, ...additionalContexts];
	}

	async getBaseContexts(pathname?: string, useLastRoute?: boolean): Promise<SnowplowContext[]> {
		const { store } = this;
		const baseContexts: SnowplowContext[] = [];

		// PAGE CONTEXT
		if (pathname) {
			const pageTypeContext: SnowplowPageTypeContext = await this.getPageTypeContext(pathname, useLastRoute);
			baseContexts.push(pageTypeContext);
		}

		// TEST CONTEXT FOR DEV AND STAGING ENVIRONMENTS
		if (JW_CONFIG.DOMAIN === 'moviecycle.com') {
			const testIdentifier = 'development';
			const testContext = new SnowplowDevelopmentTestContext(true, testIdentifier);
			baseContexts.push(testContext);
		}

		// PROPERTY BASED
		// - login
		if (store.state.user.jwLoginId) {
			baseContexts.push(new SnowplowLoginContext(store.state.user.jwLoginId));
		}
		// - mobile
		if (this.mobileContext) {
			baseContexts.push(this.mobileContext);
		}

		return baseContexts;
	}

	getAdditionalContexts(): SnowplowContext[] {
		const { store } = this;
		const additionalContexts: SnowplowContext[] = [];

		// ROUTE BASED
		// - filter
		const activeRoute = store.state.routing.activeRoute || {};
		const routeBasedContexts = (activeRoute.meta || {}).contexts || [];
		if (routeBasedContexts.indexOf('filter') !== -1) {
			const filters = store.getters['filter/currentFilters'];
			const providerShortNames = store.state.constant.providers.map((item: Provider) => item.short_name);
			const locale = store.getters['language/locale'];
			const urlMetadata = store.getters['routing/urlMetadata'];
			const pageId = urlMetadata.id;
			const routeQuery = this.store.getters['routing/queryParams'];
			const filterContext = new SnowplowFilterContext(filters, routeQuery, pageId, locale, providerShortNames);

			additionalContexts.push(filterContext);
		}

		// - title
		if (routeBasedContexts.indexOf('title') !== -1) {
			const titleDetails = store.state.titleDetails;
			if (titleDetails.titleContext) {
				additionalContexts.push(titleDetails.titleContext);
			}
		}

		// MODULE BASED (RECOMMENDATIONS)
		const activeModuleIndex = store.state.module.activeModuleIndex;
		if (activeModuleIndex !== null) {
			const activeModule = store.getters['module/modules'][activeModuleIndex];
			const moduleContext = new SnowplowModuleContext(
				activeModule.id,
				activeModuleIndex,
				activeModule.template.technical_name,
				activeModule.discovery_session_id,
				activeModule.template!.anchor
			);
			additionalContexts.push(moduleContext);
		}

		// SESSION BASED
		const now = Date.now();
		this.sessions
			.filter(session => now - this.sessionDuration < session.timestamp)
			.forEach(session => {
				// add session context
				additionalContexts.push(session.context);
				// refresh session timestamp
				session.timestamp = now;
			});

		// PRO SUBSCRIPTION BASED
		if (store.getters['user/isPremium']) {
			additionalContexts.push(this.getFreemiumContext());
		}

		return additionalContexts;
	}

	addSessionContext(context: TrackingSession['context']) {
		this.sessions.push({
			context,
			timestamp: Date.now(),
		});
	}

	setMobileContext(context: SnowplowMobileContext) {
		this.mobileContext = context;
	}

	getDomainSessionId(): Promise<string> {
		if (typeof window === 'undefined') {
			return Promise.resolve('Unknown');
		}
		return new Promise(async resolve => {
			await Vue.$jw.ready?.waitFor(ReadyType.SNOWPLOW_INIT);

			window.snowplow(function () {
				// @ts-ignore Typescript is being annoying here
				const { cf } = this;
				const domainUserInfo = cf?.getDomainUserInfo();
				const domainSessionId = domainUserInfo?.[6] ?? 'Unknown';
				resolve(domainSessionId);
			});
		});
	}

	/**
	 * Correctly setup and return title context
	 *
	 * @param {NewTitle | PopularTitle} title
	 * @param {Season} season {optional}
	 * @param {Episode} episode {optional}
	 *
	 * @returns {SnowplowTitleContext} context
	 */
	getTitleContext(
		title: Pick<NewTitle, 'id' | 'object_type' | 'season_number'>,
		season?: Pick<Season, 'season_number'>,
		episode?: Pick<Episode, 'episode_number'>
	) {
		// Need these defaults because of TS complains
		let id: number = title.id,
			object_type: TitleObjectType = title.object_type,
			season_number = null,
			episode_number = null;

		if (season && season.season_number) {
			season_number = season.season_number;
		}

		if (episode?.episode_number) {
			episode_number = episode.episode_number;
		}

		return new SnowplowTitleContext(id, object_type, season_number, episode_number);
	}

	getTitleContextGraphql(
		titleObjectId: number,
		titleObjectType: ObjectType,
		seasonNumber?: number,
		episodeNumber?: number
	) {
		// Need these defaults because of TS complains
		let id = -1,
			objectType: ObjectType = ObjectType.Movie;
		const formattedSeasonNumber = seasonNumber ? seasonNumber : null;
		const formattedEpisodeNumber = episodeNumber ? episodeNumber : null;

		id = titleObjectId;
		objectType = titleObjectType;

		return new SnowplowTitleContextGraphql(id, objectType, formattedSeasonNumber, formattedEpisodeNumber);
	}

	getFreemiumContext() {
		const isHideSeenDislikedEnabled: boolean = this.store.getters['user/isHideSeenDislikedTitlesEnabled'];

		return new SnowplowFreemiumContext(isHideSeenDislikedEnabled);
	}

	getPackageContext(
		packageId: number,
		packageName: string,
		parentType?: null | 'package' | 'bundle',
		parentId?: number,
		parentName?: string
	) {
		return new SnowplowPackageContext(packageId, packageName, parentType, parentId, parentName);
	}

	/**
	 * Correctly setup and return page type context for a given page
	 *
	 *
	 * @returns {SnowplowPageTypeContext} context
	 */
	async getPageTypeContext(pathname: string, useLastRoute = false): Promise<SnowplowPageTypeContext> {
		const { store } = this;
		const language: string = store.getters['language/language'];
		const country: string = store.getters['language/country'];
		const routeMetaData = useLastRoute ? store.state.routing.lastUrlMetadata : store.state.routing.urlMetadata;
		const metaData: UrlMetadataResponse<HtmlContentMovieDetails> = routeMetaData;
		const onboardingStatus: OnboardingStatus | null = store.state.user.onboarding_status;
		const technical_name: (metaData: any) => string = (metaData: any) => {
			// Global routes don't have metadata, so we need to have exception for such routes
			if (pathname.startsWith('/shared')) {
				return 'VIEW_SHARED_LIST';
			}

			//checks if page is home
			if (!metaData || (pathname === '/' && !metaData.hasOwnProperty('page'))) {
				return `VIEW_HOME_${onboardingStatus}`;
			} else {
				if (metaData && metaData.hasOwnProperty('page')) {
					return metaData.page.technical_name as string;
				} else {
					return metaData.object_type as string;
				}
			}
		};
		let pageType: string = technical_name(metaData);

		if (!pageType) {
			/**
			 * hacky way to avoid empty pageType
			 * Poll until we get a valid pageType
			 *
			 * TO-DO refactor of the chain events that lead to this bug
			 * this happens due to the fact that SET_URL_METADATA_PROMISE completly remove the old promise
			 * resulting in an empty metaData
			 */
			const timeout = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
			const checkMetaData: any = async () => {
				await timeout(100);

				const metaData: UrlMetadataResponse<HtmlContentMovieDetails> = routeMetaData;
				pageType = technical_name(metaData);

				if (pageType) {
					return Promise.resolve(pageType);
				}

				return checkMetaData();
			};

			await checkMetaData();
		}

		return new SnowplowPageTypeContext(pageType, country, language);
	}

	/**
	 * check if not_bounced needs to be sent as well.
	 */
	private checkNotBounced() {
		if (!this.notBounced) {
			this.notBounced = true;
			this.trackEvent('not_bounced', {
				nonInteraction: true,
			});
		}
	}

	/**
	 * checks if campaign contexts should be added for this session.
	 */
	private handleAddingCampaignContexts() {
		if (typeof window === 'undefined') {
			return;
		}

		const uri = window.location.search.substring(1);
		const params = new URLSearchParams(uri);

		if (params.get('uct_b')) {
			// add campaign context v2
			const campaignContext = new SnowplowCampaignContextV2(
				params.get('uct_b')!,
				params.get('uct_s')!,
				params.get('uct_i')!,
				params.get('uct_at')!,
				params.get('uct_tn')!,
				`${window.location.protocol}//${window.location.host}${window.location.pathname}`,
				params.get('uct_p'),
				params.get('uct_sid') ? parseInt(params.get('uct_sid')!) : null,
				params.get('uct_cid') ? parseInt(params.get('uct_cid')!) : null,
				params.get('uct_adgid') ? parseInt(params.get('uct_adgid')!) : null,
				params.get('uct_crid') ? parseInt(params.get('uct_crid')!) : null,
				params.get('uct_adid') ? parseInt(params.get('uct_adid')!) : null,
				params.get('uct_ssid') ? parseInt(params.get('uct_ssid')!) : null,
				params.get('uct_scid') ? parseInt(params.get('uct_scid')!) : null,
				params.get('uct_eccid') ? parseInt(params.get('uct_eccid')!) : null
			);
			this.addSessionContext(campaignContext);
		}
	}

	isGDPRCountry(webLocale: WebLocale) {
		return GDPR_WEB_LOCALES.includes(webLocale);
	}
}

// return singleton of trackinghelper
export const TrackingHelper = new TrackingHelperClass();

/* CWV Monitoring */
export async function initCoreWebVitalsMonitor() {
	const { getCLS, getFCP, getFID, getLCP, getTTFB } = await import('web-vitals');

	function sendEvent(data: Set<Metric>) {
		TrackingHelper.trackEvent('corewebvitals', { nonInteraction: true }, [new SnowplowCoreWebVitalsContext(data)]);
	}

	const queue = new Set<Metric>();
	function addToQueue(metric: Metric) {
		queue.add(metric);
	}

	function flushQueue() {
		if (queue.size === 0) return;

		sendEvent(queue);
		queue.clear();
	}

	getCLS(addToQueue, true);
	getFCP(addToQueue);
	getTTFB(addToQueue);
	getLCP(addToQueue);
	getFID(addToQueue);

	// Report all available metrics whenever the page is backgrounded or unloaded.
	addEventListener('visibilitychange', () => {
		if (document.visibilityState !== 'hidden') return;
		flushQueue();
	});

	// NOTE: Safari does not reliably fire the `visibilitychange` event when the
	// page is being unloaded. If Safari support is needed, you should also flush
	// the queue in the `pagehide` event.
	addEventListener('pagehide', flushQueue);
}
